import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  Inject,
  Injectable,
  NgZone,
  Optional,
  PLATFORM_ID,
  TransferState,
  makeStateKey,
} from '@angular/core';
import { MapDirectionsService, MapGeocoder } from '@angular/google-maps';
import type { MapCoordinates } from '@freelancer/datastore';
import { enterZone } from '@freelancer/time-utils';
import { Loader } from '@googlemaps/js-api-loader';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {} from 'google.maps';
import { Observable, firstValueFrom, queueScheduler } from 'rxjs';
import { filter, map, observeOn } from 'rxjs/operators';
import { GOOGLE_MAPS_CONFIG, SSR_MAPS_API_KEY } from './google-maps.config';
import { GoogleMapsConfig } from './google-maps.interface';
// Subset of methods on google.maps.places.Autocomplete. Add more if needed.
export interface GoogleMapsAutocomplete {
  /* A multicasted observable of the PlaceResult returned by autocomplete */
  placeChanged(): Observable<google.maps.places.PlaceResult>;
  setComponentRestrictions(
    restrictions: google.maps.places.ComponentRestrictions,
  ): void;
}

export interface GoogleMapsGeocodeResponse {
  results: readonly google.maps.GeocoderResult[];
  status: google.maps.GeocoderStatus;
}

export interface GoogleMapStaticImage {
  styledMapUrl: string | undefined;
  mapUrl: string | undefined;
}

export interface AutocompleteResponse {
  readonly result: google.maps.places.AutocompletePrediction[];
  readonly status: google.maps.places.PlacesServiceStatus;
}

enum TravelMode {
  DRIVING = 'DRIVING',
  TRANSIT = 'TRANSIT',
  WALKING = 'WALKING',
}

@UntilDestroy({ className: 'GoogleMaps' })
@Injectable({
  providedIn: 'root',
})
/**
 * Wrapper around the Google Maps JavaScript API.
 * Call `load` to bootstrap the Maps API before calling any other methods.
 *
 * @see https://developers.google.com/maps/documentation/javascript/tutorial
 *
 * NOTE: Keep in sync with GoogleMapsTesting
 */
export class GoogleMaps {
  private loaded = false;

  constructor(
    @Inject(GOOGLE_MAPS_CONFIG) private googleMapsConfig: GoogleMapsConfig,
    private geocoder: MapGeocoder,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object,
    private transferState: TransferState,
    private directionsService: MapDirectionsService,
    @Optional() @Inject(SSR_MAPS_API_KEY) private ssrMapsApiKey?: string,
  ) {}

  /**
   * Loads the Google Maps API onto the page.
   * No-op and returns false on the server.
   */
  async load(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    if (this.loaded) {
      return true;
    }

    const loader = new Loader({
      apiKey: this.googleMapsConfig.apiKey,
      version: 'weekly',
      libraries: ['places', 'geocoding', 'routes'],
    });

    // `loader.load()` has been deprecated in favour of dynamic library imports.
    // However, the angular-google-map component expects the API to be loaded before it can be displayed.
    // Use the loader library to trigger the initial loading of the maps API.
    // https://github.com/angular/components/issues/17828
    await loader.importLibrary('maps');

    this.loaded = true;
    return true;
  }

  /**
   * Creates an Autocomplete object, which is a text input field that returns
   * place predictions as the user types.
   *
   * Wrapper around `google.maps.places.Autocomplete`.
   *
   * @see https://developers.google.com/maps/documentation/javascript/places-autocomplete
   */
  createAutocomplete(
    inputField: HTMLInputElement,
    options?: google.maps.places.AutocompleteOptions,
  ): GoogleMapsAutocomplete {
    if (this.isNotLoaded()) {
      throw new Error(
        'Maps API is not loaded yet. Call GoogleMaps.load() first.',
      );
    }

    const autocomplete = new google.maps.places.Autocomplete(
      inputField,
      options,
    );
    let placeChangedListener: google.maps.MapsEventListener | undefined;

    const placeChanged$ = new Observable<google.maps.places.PlaceResult>(
      observer => {
        placeChangedListener = autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          observer.next(place);
        });

        // teardown logic
        return () => {
          if (placeChangedListener) {
            placeChangedListener.remove();
          }
          observer.unsubscribe();
        };
      },
    ).pipe(
      // Re-enter the Angular zone so clients don't have to call `ngZone.run`.
      // QueueScheduler because we are only using this operator to re-enter, and
      // don't actually want to emit values asynchronously
      observeOn(enterZone(this.ngZone, queueScheduler)),
    );

    return {
      placeChanged: () => placeChanged$,
      setComponentRestrictions: (
        restrictions: google.maps.places.ComponentRestrictions,
      ) => {
        autocomplete.setComponentRestrictions(restrictions);
      },
    };
  }

  /**
   * Converts a human-readable address (e.g. "680 George Street, Sydney, NSW")
   * into geographic coordinates (e.g. latitude -33.8771501, longitude 151.2062187)
   *
   * @see https://developers.google.com/maps/documentation/javascript/geocoding
   */
  async geocode(
    request: google.maps.GeocoderRequest,
  ): Promise<GoogleMapsGeocodeResponse> {
    await this.load();

    return firstValueFrom(
      this.geocoder.geocode(request).pipe(untilDestroyed(this)),
    );
  }

  // Maps API can only be loaded on the browser
  private isNotLoaded(): boolean {
    return isPlatformBrowser(this.platformId) && !this.loaded;
  }

  async getDirections(
    origin: MapCoordinates | undefined,
    destination: MapCoordinates | undefined,
  ): Promise<google.maps.DirectionsResult | undefined> {
    const loaded = await this.load();
    if (!loaded) {
      return undefined;
    }

    if (!origin || !destination) {
      return undefined;
    }

    return firstValueFrom(
      this.directionsService
        .route({
          origin: { lat: origin?.latitude, lng: origin.longitude },
          destination: {
            lat: destination.latitude,
            lng: destination.longitude,
          },
          travelMode: TravelMode.DRIVING,
        })
        .pipe(
          filter(
            response =>
              response.status === google.maps.DirectionsStatus.OK ||
              response.status === google.maps.DirectionsStatus.ZERO_RESULTS,
          ),
          map(response => response.result),
          untilDestroyed(this),
        ),
    );
  }

  /**
   * Returns the Google Maps Static Map URL which can be embedded in
   * <img> tag's src attribute, or it's equivalent.
   * Primarily, this will be used in server-side rendering.
   *
   * @see https://developers.google.com/maps/documentation/maps-static/start
   */
  async generateStaticMapUrl(
    origin: MapCoordinates | undefined,
    originMarkerIcon: string,
    destination: MapCoordinates | undefined,
    destinationMarkerIcon: string,
    key: string,
  ): Promise<GoogleMapStaticImage | undefined> {
    if (!origin && !destination) {
      return undefined;
    }

    const stateKey = makeStateKey<GoogleMapStaticImage>(
      `${key}-googleMapStaticUrl`,
    );

    const staticMapUrlState = this.transferState.get(stateKey, {
      styledMapUrl: undefined,
      mapUrl: undefined,
    });
    if (staticMapUrlState.styledMapUrl && staticMapUrlState.mapUrl) {
      return staticMapUrlState;
    }

    // style parameters based on @freelancer/ui/map/themes.ts
    const mapStyle =
      'style=feature%3Aadministrative%7Celement%3Ageometry.fill%7Ccolor%3A0xd6e2e6%7C&style=feature%3Aadministrative%7C' +
      'element%3Ageometry.stroke%7Ccolor%3A0xcfd4d5%7C&style=feature%3Aadministrative%7Celement%3Alabels.text.fill%7Ccolor%3A' +
      '0x7492a8%7C&style=feature%3Aadministrative.neighborhood%7Celement%3Alabels.text.fill%7Clightness%3A25%7C&style=feature%3A' +
      'landscape.man_made%7Celement%3Ageometry.fill%7Ccolor%3A0xdde2e3%7C&style=feature%3Alandscape.man_made%7Celement%3A' +
      'geometry.stroke%7Ccolor%3A0xcfd4d5%7C&style=feature%3Alandscape.natural%7Celement%3Ageometry.fill%7Ccolor%3A0xdde2e3%7C' +
      '&style=feature%3Alandscape.natural%7Celement%3Alabels.text.fill%7Ccolor%3A0x7492a8%7C&style=feature%3Alandscape.natural.terrain%7C' +
      'element%3Aall%7Cvisibility%3Aoff%7C&style=feature%3Apoi%7Celement%3Aall%7Cvisibility%3Aon%7C&style=feature%3Apoi%7Celement%3A' +
      'geometry.fill%7Ccolor%3A0xdde2e3%7C&style=feature%3Apoi%7Celement%3Alabels.text.fill%7Ccolor%3A0x588ca4%7C&style=feature%3Apoi%7C' +
      'element%3Alabels.icon%7Csaturation%3A-100%7C&style=feature%3Apoi.park%7Celement%3Ageometry.fill%7Ccolor%3A0xa9de83%7C&style=feature%3A' +
      'poi.park%7Celement%3Ageometry.stroke%7Ccolor%3A0xbae6a1%7C&style=feature%3Apoi.sports_complex%7Celement%3Ageometry.fill%7Ccolor%3A' +
      '0xc6e8b3%7C&style=feature%3Apoi.sports_complex%7Celement%3Ageometry.stroke%7Ccolor%3A0xbae6a1%7C&style=feature%3Aroad%7Celement%3A' +
      'labels.text.fill%7Ccolor%3A0x41626b%7C&style=feature%3Aroad%7Celement%3Alabels.icon%7Csaturation%3A-45%7Clightness%3A10%7Cvisibility%3A' +
      'on%7C&style=feature%3Aroad.highway%7Celement%3Ageometry.fill%7Ccolor%3A0xc1d1d6%7C&style=feature%3Aroad.highway%7Celement%3A' +
      'geometry.stroke%7Ccolor%3A0xa6b5bb%7C&style=feature%3Aroad.highway%7Celement%3Alabels.icon%7Cvisibility%3Aon%7C&style=feature%3A' +
      'road.highway.controlled_access%7Celement%3Ageometry.fill%7Ccolor%3A0x9fb6bd%7C&style=feature%3Aroad.arterial%7Celement%3A' +
      'geometry.fill%7Ccolor%3A0xffffff%7C&style=feature%3Aroad.local%7Celement%3Ageometry.fill%7Ccolor%3A0xffffff%7C&style=feature%3A' +
      'transit%7Celement%3Alabels.icon%7Csaturation%3A-70%7C&style=feature%3Atransit.line%7Celement%3Ageometry.fill%7Ccolor%3A0xb4cbd4%7C' +
      '&style=feature%3Atransit.line%7Celement%3Alabels.text.fill%7Ccolor%3A0x588ca4%7C&style=feature%3Atransit.station%7Celement%3A' +
      'labels.text.fill%7Ccolor%3A0x008cb5%7C&style=feature%3Atransit.station.airport%7Celement%3Ageometry.fill%7Csaturation%3A-100%7C' +
      'lightness%3A-5%7C&style=feature%3Awater%7Celement%3Ageometry.fill%7Ccolor%3A0xa6cbe3%7C';

    if (isPlatformServer(this.platformId)) {
      if (this.ssrMapsApiKey === undefined) {
        throw new Error('SSR_MAPS_API_KEY is not defined on the server.');
      }
      // TODO: T310915 - Redo route query and polyline generation
      // Google map directions API call used to be here but was removed in T305112 after a Google update broke the polyline implementation
    }

    const staticMapUrlStyled =
      `https://maps.googleapis.com/maps/api/staticmap?size=600x450&scale=2&format=png` +
      `&markers=icon:${originMarkerIcon}%7C${origin?.latitude},${origin?.longitude}` +
      `&markers=icon:${destinationMarkerIcon}%7C${destination?.latitude},${destination?.longitude}` +
      `&${mapStyle}` +
      `&key=${this.googleMapsConfig.apiKey}`;

    const staticMapUrl =
      `https://maps.googleapis.com/maps/api/staticmap?size=600x450&scale=2&format=png` +
      `&markers=${origin?.latitude},${origin?.longitude}%7C${destination?.latitude},${destination?.longitude}` +
      `&key=${this.googleMapsConfig.apiKey}`;

    this.transferState.set(stateKey, {
      styledMapUrl: staticMapUrlStyled,
      mapUrl: staticMapUrl,
    });

    return {
      styledMapUrl: staticMapUrlStyled,
      mapUrl: staticMapUrl,
    };
  }

  /**
   * This sends request to AutocompleteService and returns list of place predictions from user's query.
   */
  async getAutocompletePredictions(
    request: google.maps.places.AutocompletionRequest,
  ): Promise<AutocompleteResponse> {
    await this.load();
    const autocompleteService = new google.maps.places.AutocompleteService();

    return new Promise(resolve => {
      autocompleteService.getPlacePredictions(
        {
          input: request.input,
          language: request.language,
          componentRestrictions: request.componentRestrictions,
          types: request.types,
        },
        (result, status) => {
          resolve({ result: result ?? [], status });
        },
      );
    });
  }

  /**
   * Returns details of place by ID. This also handles fetching postcode if first request lacks it.
   */
  async getLocation(
    placeId: string,
    postalCodeRequired: boolean,
    language?: string | undefined,
  ): Promise<google.maps.GeocoderResult | undefined> {
    const response = await this.geocode({
      placeId,
      language,
    });

    if (response.status !== google.maps.GeocoderStatus.OK) {
      return undefined;
    }

    // Querying by ID will ensure one result
    const placeById = response.results[0];

    // Handle fetching with coordinates if place by ID has no postcode
    if (
      postalCodeRequired &&
      !placeById.address_components.find(component =>
        component.types.find(type => type === 'postal_code'),
      )
    ) {
      const responseFromCoordinates = await this.geocode({
        location: placeById.geometry.location,
        language,
      });

      const resultsByCoordinates = responseFromCoordinates.results;

      const placeByCoordinates = resultsByCoordinates.find(value =>
        value.address_components.find(component =>
          component.types.find(type => type === 'postal_code'),
        ),
      );

      const postalCode = placeByCoordinates
        ? placeByCoordinates.address_components.find(component =>
            component.types.find(type => type === 'postal_code'),
          )
        : undefined;

      if (postalCode) {
        return {
          ...placeById,
          ...{
            address_components: placeById.address_components.concat([
              postalCode,
            ]),
          },
        };
      }
    }

    return placeById;
  }
}
